import { Component, ElementRef, HostListener, ChangeDetectorRef, OnInit, OnDestroy, AfterViewChecked, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { GainsToastService } from 'app/shared/services/gains-toast-service/gains-toast.service';
import { DEOAiAssistantService } from 'app/shared/screens/deo-ai-assistant/services/deo-ai-assistant.service';
import { AlertAskDeoContext } from 'app/shared/models/alert-ask-deo-context.model';
import { AgentsService } from 'app/modules/agent-library/services/agents.service';
import { AgentDefinition } from 'app/modules/agent-library/models/agent-definition.model';
import { AgentRunHistoryEntry } from 'app/modules/agent-library/models/agent-run.model';
import { ChatMessage, ChatSession } from '../models/chat.models';
import { DEO_TARGET_ID, TalkingToTarget } from '../models/talking-to-target.model';
import { DeoAgentReplyService } from '../services/deo-agent-reply.service';
import { buildTalkingToTargets } from '../services/talking-to-targets.helper';
import { runDateOf } from '../services/agent-runs.helper';
import { ConversationStreamEvent, DeoConversation } from '../models/deo-conversation.model';
import { AIAgentService } from '../services/ai-agent.service';
import { getApiErrorMessage } from '../services/api-error.helper';
import { formatStreamActivityLabel } from '../services/stream-activity.helper';
import { pendingClarificationOptions } from '../services/clarification.helper';
import { DeoConversationService } from '../services/deo-conversation.service';
import { VoiceToTextService } from 'app/shared/services/voice-to-text.service';
import { MessageFormatterService } from '../services/message-formatter.service';
import { FileSelectionService } from 'app/shared/services/file-selection/file-selection.service';
import { SelectOption } from '@gainsystems/gains-ui-commons';
import { FeatureFlagsService } from 'app/shared/services/feature-flags/feature-flags.service';
import { formatChatMessageTimestamp } from 'app/shared/util/chat-timestamp.util';
import { keepInFlightChats } from '../models/temp-session.constants';
import {
  FEATURE_FLAG_DEO_AGENT_ATTACH_FILE,
  FEATURE_FLAG_DEO_AGENT_LIVE_TRANSCRIPTION,
  FEATURE_FLAG_DEO_AGENT_CONVERSATION_CHIPS,
  FEATURE_FLAG_DEO_AGENT_STOP_BUTTON,
  FEATURE_FLAG_DEO_AGENT_TALKING_TO
} from 'app/shared/services/feature-flags/feature-flags.constants';

@Component({
  selector: 'deo-ai-assistant-widget',
  standalone: false,
  templateUrl: './deo-ai-assistant-widget.component.html',
  styleUrls: ['./deo-ai-assistant-widget.component.scss']
})
export class DeoAiAssistantWidgetComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesContainer') private readonly messagesContainer?: ElementRef;

  private readonly NEW_CHAT_OPTION: SelectOption = { title: 'New Chat', value: 'new-chat' };

  /**
   * Marks a dropdown option as one of the selected agent's runs rather than a DEO chat session.
   *
   * The two live in the same control, and a run id is not a session id — loading one as chat history
   * would ask the backend for a session that does not exist.
   */
  private readonly RUN_OPTION_PREFIX = 'agent-run:';
  private readonly MAX_PREVIOUS_SESSIONS = 5;
  private _isOpen = false;
  private readonly _destroy$ = new Subject<void>();

  /** Whether the reader opened or shut a message's progress panel; otherwise it follows the answer. */
  private readonly _progressCollapsedByUser = new WeakMap<ChatMessage, boolean>();

  get isOpen(): boolean {
    return this._isOpen;
  }

  set isOpen(value: boolean) {
    this._isOpen = value;
  }

  currentMessage = '';
  /** The conversation currently shown in the widget; its live messages survive close/reopen and are shared with the full screen. */
  currentConversation: DeoConversation | null = null;
  currentSessionId: string | null = null;

  // File attachment
  selectedFile: File | null = null;
  readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

  // Chat dropdown properties
  chatSelectControl = new FormControl();
  chatOptions: SelectOption[] = [];
  chatSessions: ChatSession[] = [];

  // Voice recording properties
  isListening = false;
  voiceError: string | null = null;
  isLiveTranscriptionEnabled = false;
  isAttachFileEnabled = false;
  isStopButtonEnabled = false;
  isTalkingToEnabled = false;

  /**
   * The follow-up chips, on their own flag.
   *
   * Separate from `isTalkingToEnabled` on purpose: the chips are a distinct piece of work waiting on
   * backend support, so they can stay dark long after the selector and the agent replies ship.
   */
  isConversationChipsEnabled = false;

  private _baseText = '';

  /** Every agent the catalogue holds; the selector shows the ones a planner can actually talk to. */
  private _agents: AgentDefinition[] = [];

  // ===== Scroll Management =====
  private shouldAutoScroll = true;

  constructor(
    private readonly _elementRef: ElementRef,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _aIAgentService: AIAgentService,
    private readonly _voiceService: VoiceToTextService,
    private readonly _gainsToastService: GainsToastService,
    private readonly _dEOAiAssistantService: DEOAiAssistantService,
    private readonly _messageFormatter: MessageFormatterService,
    private readonly _fileSelectionService: FileSelectionService,
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _conversationService: DeoConversationService,
    private readonly _agentsService: AgentsService,
    private readonly _agentReplyService: DeoAgentReplyService
  ) {}

  /**
   * Who the widget is talking to: `deo`, or an agent id.
   *
   * Mirrored onto `DEOAiAssistantService` so it outlives this component; the service is the source
   * of truth, this field is what the template binds.
   */
  selectedTargetId: string = DEO_TARGET_ID;

  /** The selector's rows, shared with the full screen's own selector. */
  get talkingToTargets(): TalkingToTarget[] {
    return buildTalkingToTargets(this._agents, this.selectedTargetId, name => this._agentsService.resolveAgentMark(name));
  }

  /** The agent being talked to, or undefined when the target is DEO itself. */
  get selectedAgent(): AgentDefinition | undefined {
    return this._agents.find(agent => agent.id === this.selectedTargetId);
  }

  /** The composer says who it is addressing, as the design has it. */
  get composerPlaceholder(): string {
    return this.selectedAgent ? `Ask ${this.selectedAgent.name} about its findings…` : 'Message DEO...';
  }

  /**
   * The follow-up prompts to offer under a message, if any.
   *
   * Empty unless the turn was answered by an agent that offered some and the chips flag is on — the
   * chips are their own piece of work, waiting on backend support.
   */
  followUpsFor(message: ChatMessage): string[] {
    if (!this.isConversationChipsEnabled) {
      return [];
    }

    return message.agentReply?.followUps ?? [];
  }

  /** Quick replies to a clarifying question, offered under the last message while it is unanswered. */
  get clarificationOptions(): string[] {
    return this.isConversationChipsEnabled ? pendingClarificationOptions(this.messages) : [];
  }

  isLastMessage(message: ChatMessage): boolean {
    return this.messages.at(-1) === message;
  }

  /** Who the bubble is attributed to: the reader, the answering agent, or DEO. */
  messageAuthor(message: ChatMessage): string {
    if (message.role === 'user') {
      return 'You';
    }

    return message.agentReply?.agentName ?? 'DEO AI Assist';
  }

  /**
   * Keyed on the persisted id where there is one, falling back to position.
   *
   * The list had no `trackBy` at all, which was survivable while a message was a line of text; a
   * reply now carries a driver list and a suggested path, and `streamEvents$` fires once per
   * typewriter frame, so re-creating every row on each frame is not.
   */
  trackByMessage(index: number, message: ChatMessage): string {
    return message.id ?? `index-${index}`;
  }

  /** Messages render from the live conversation; the streaming engine in DeoConversationService mutates it in place. */
  get messages(): ChatMessage[] {
    return this.currentConversation?.messages ?? [];
  }

  /** True while this conversation is producing a response (the engine lives in DeoConversationService). */
  get isStreaming(): boolean {
    return this._conversationService.isStreaming(this.currentConversation);
  }

  /** True while any answer is being produced, streamed or answered locally by an agent. */
  get isBusy(): boolean {
    return this._conversationService.isBusy(this.currentConversation);
  }

  /** Retained for the template's disabled states; streaming now covers the whole send-to-finish window. */
  get isLoading(): boolean {
    return this.isBusy;
  }

  /** Retained for the template's disabled states; mirrors the streaming flag. */
  get isTyping(): boolean {
    return this.isBusy;
  }

  // ── Input bar state ──────────────────────────────────────────────
  // The footer button is either "send" (idle) or "stop" (a response is streaming).

  /** True while the assistant is producing a response (sending, streaming tokens, or an agent answering). */
  get isResponseInProgress(): boolean {
    return this.isBusy;
  }

  /** Show the stop button only when the feature is enabled and a *streamed* response is running. */
  get canStopResponse(): boolean {
    return this.isStopButtonEnabled && this.isStreaming;
  }

  /** The send button is disabled when there is nothing to send or a response is already in progress. */
  get isSendDisabled(): boolean {
    return !this.currentMessage.trim() || this.isResponseInProgress;
  }

  private transformChatSessionsToOptions(sessions: ChatSession[]): void {
    const options = sessions
      .filter((session: ChatSession) => {
        const normalizedTitle = session.title?.trim().toLowerCase();
        const normalizedId = session.id?.trim().toLowerCase();
        return normalizedTitle !== this.NEW_CHAT_OPTION.title.toLowerCase() && normalizedId !== String(this.NEW_CHAT_OPTION.value).toLowerCase();
      })
      .slice(0, this.MAX_PREVIOUS_SESSIONS)
      .map((session: ChatSession) => ({
        title: session.title,
        value: session.id
      }));

    this.chatOptions = [this.NEW_CHAT_OPTION, ...options];
  }

  private syncSelectedChatOption(): void {
    if (!this.currentSessionId) {
      this.chatSelectControl.setValue(this.NEW_CHAT_OPTION, { emitEvent: false });
      return;
    }

    const selectedSessionOption = this.chatOptions.find(option => option.value === this.currentSessionId);

    if (selectedSessionOption) {
      this.chatSelectControl.setValue(selectedSessionOption, { emitEvent: false });
      return;
    }

    const currentSelection = this.chatSelectControl.value as SelectOption | null;
    const fallbackTitle = currentSelection?.value === this.currentSessionId ? currentSelection.title : 'Current Chat';
    const fallbackOption: SelectOption = {
      title: fallbackTitle,
      value: this.currentSessionId
    };

    const nonNewChatOptions = this.chatOptions.filter(option => option.value !== this.NEW_CHAT_OPTION.value && option.value !== this.currentSessionId);
    this.chatOptions = [this.NEW_CHAT_OPTION, fallbackOption, ...nonNewChatOptions].slice(0, this.MAX_PREVIOUS_SESSIONS + 1);
    this.chatSelectControl.setValue(fallbackOption, { emitEvent: false });
  }

  /**
   * The dropdown's options for the agent being talked to: its own recorded runs.
   *
   * An agent's session history is its runs — there is no per-agent chat thread to list, since a
   * conversation belongs to DEO and the agent turns here are not persisted. Both fixture runs share
   * the same scenario, so the date from `meta` is what tells them apart in a one-line option.
   */
  private transformAgentRunsToOptions(agent: AgentDefinition): void {
    const options = agent.runHistory.slice(0, this.MAX_PREVIOUS_SESSIONS).map(run => ({
      title: `${run.scenario} — ${runDateOf(run)}`,
      value: `${this.RUN_OPTION_PREFIX}${run.id}`
    }));

    this.chatOptions = [this.NEW_CHAT_OPTION, ...options];
    this.chatSelectControl.setValue(this.NEW_CHAT_OPTION, { emitEvent: false });
  }

  /**
   * Opens a recorded run as a conversation.
   *
   * A run is a transcript, not an empty thread: picking one used to prefill the composer and leave
   * the message area blank, which read as a broken chat. It loads the run's own turns instead, and
   * the composer is left empty so the next question is the reader's.
   */
  private openRun(optionValue: string): void {
    const agent = this.selectedAgent;
    const runId = optionValue.slice(this.RUN_OPTION_PREFIX.length);
    const run = agent?.runHistory.find(candidate => candidate.id === runId);
    if (!agent || !run) {
      return;
    }

    // A run has no backend session, so nothing must try to sync it as one.
    this.currentSessionId = null;
    this.currentConversation = this._conversationService.openAgentRun(agent, run);
    this._restoreComposer();
    this.shouldAutoScroll = true;
    this._cdr.markForCheck();
  }

  /** Rebuilds the dropdown for whoever is being talked to now. */
  private refreshSessionOptions(): void {
    const agent = this.selectedAgent;

    if (agent) {
      this.transformAgentRunsToOptions(agent);
      return;
    }

    this.transformChatSessionsToOptions(this.chatSessions);
    this.syncSelectedChatOption();
  }

  loadChatSessions(): void {
    this._aIAgentService.getChatSessions().subscribe({
      next: response => {
        if (response.succeeded) {
          this.chatSessions = keepInFlightChats(this.chatSessions, response.data);

          // Held for when the target goes back to DEO, but not shown over an agent's runs.
          if (!this.selectedAgent) {
            this.transformChatSessionsToOptions(this.chatSessions);
            this.syncSelectedChatOption();
          }
        }
      },
      error: error => {
        this._gainsToastService.error('Error', getApiErrorMessage(error, 'Failed to load chat sessions'));
      }
    });
  }

  ngOnInit(): void {
    this.isLiveTranscriptionEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_LIVE_TRANSCRIPTION);
    this.isAttachFileEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_ATTACH_FILE);
    this.isStopButtonEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_STOP_BUTTON);
    this.isTalkingToEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_TALKING_TO);
    this.isConversationChipsEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_CONVERSATION_CHIPS);

    if (this.isTalkingToEnabled) {
      // Nothing else loads the catalogue: a planner who has never opened /agent-library would
      // otherwise get an empty selector.
      this._agentsService.agents$.pipe(takeUntil(this._destroy$)).subscribe(agents => {
        this._agents = agents;
        // The catalogue is what turns a restored agent id into a run list.
        this.refreshSessionOptions();
        this._cdr.markForCheck();
      });

      // The selection lives on the service, so it survives this widget being destroyed and rebuilt
      // (which happens on a trip to /deo-ai-assist and back). Restored before the catalogue loads,
      // so the subscription above already has it to work from.
      this.selectedTargetId = this._dEOAiAssistantService.getSelectedAgentId() ?? DEO_TARGET_ID;

      this._agentsService.loadAgents();
    }

    this._conversationService.streamEvents$.pipe(takeUntil(this._destroy$)).subscribe(event => this._onConversationEvent(event));

    this._dEOAiAssistantService.alertContext$.pipe(takeUntil(this._destroy$)).subscribe(context => this._applyAlertContext(context));

    this._dEOAiAssistantService.isOpen$.pipe(takeUntil(this._destroy$)).subscribe(isOpen => {
      this.isOpen = isOpen;
      if (isOpen) {
        this.loadChatSessions();

        if (this.messages.length > 0) {
          this.shouldAutoScroll = true;
          setTimeout(() => this.scrollToBottom(), 0);
        }
      }
      this._cdr.markForCheck();
    });

    this.chatSelectControl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((selectedOption: SelectOption | null) => {
      const value = selectedOption?.value ? String(selectedOption.value) : '';
      if (!value) {
        this.startNewChatFromClear();
        return;
      }

      // A run is not a chat session: asking the backend to load one would 404.
      if (value.startsWith(this.RUN_OPTION_PREFIX)) {
        this.openRun(value);
        return;
      }

      this.loadChatHistory(value);
    });

    this._voiceService.isListening$.pipe(takeUntil(this._destroy$)).subscribe(isListening => {
      this.isListening = isListening;
      this._cdr.markForCheck();
    });

    this._voiceService.transcription$.pipe(takeUntil(this._destroy$)).subscribe(transcription => {
      if (transcription.isFinal) {
        this._baseText = this._baseText ? this._baseText + ' ' + transcription.transcript : transcription.transcript;
        this.onMessageChange(this._baseText);
      } else {
        this.onMessageChange(this._baseText ? this._baseText + ' ' + transcription.transcript : transcription.transcript);
      }
      this._cdr.markForCheck();
    });

    this._voiceService.error$.pipe(takeUntil(this._destroy$)).subscribe(error => {
      this.voiceError = error;
      this._cdr.markForCheck();

      if (error) {
        setTimeout(() => {
          this.voiceError = null;
          this._cdr.markForCheck();
        }, 5000);
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Reacts to engine events. Completions update the chat dropdown regardless of which chat is open, so a
   * new chat that finishes after the user navigated away still appears (no need to reopen the widget).
   * Scrolling and error toasts are view concerns, so they only apply to the conversation in view.
   */
  private _onConversationEvent(event: ConversationStreamEvent): void {
    if (event.type === 'completed') {
      this._onConversationCompleted(event);
      return;
    }

    if (!this.currentConversation || event.clientId !== this.currentConversation.clientId) {
      return;
    }

    if (event.type === 'updated') {
      this.shouldAutoScroll = true;
      this._cdr.markForCheck();
    } else if (event.type === 'registered') {
      this._onConversationRegistered(event);
    } else if (event.type === 'error') {
      this._gainsToastService.error('Error', event.error);
      this._cdr.markForCheck();
    }
  }

  /**
   * Gives the chat the user just started its dropdown entry, titled with the question they asked, so they
   * can switch to another chat and still come back to this one while its answer streams. The entry is
   * swapped for the resolved session on completion.
   *
   * A question answered by a named agent never reaches the engine, so it registers nothing — an agent
   * turn is not a chat session.
   */
  private _onConversationRegistered(event: Extract<ConversationStreamEvent, { type: 'registered' }>): void {
    this.currentSessionId = event.sessionId;
    this.chatSessions.unshift({
      id: event.sessionId,
      title: event.title,
      created_at: new Date(),
      last_message_at: new Date(),
      user_id: 'current-user',
      artifactCount: 0
    });
    this.transformChatSessionsToOptions(this.chatSessions);
    this.syncSelectedChatOption();
    this._cdr.markForCheck();
  }

  private _onConversationCompleted(event: Extract<ConversationStreamEvent, { type: 'completed' }>): void {
    const isViewing = this.currentConversation?.clientId === event.clientId;

    if (event.isNew) {
      // Swap the temporary dropdown entry for the resolved session so the chat stays reachable.
      this.chatSessions = this.chatSessions.filter(session => session.id !== event.previousSessionId);
      if (!this.chatSessions.find(session => session.id === event.sessionId)) {
        this.chatSessions.unshift({
          id: event.sessionId,
          title: event.title,
          created_at: new Date(),
          last_message_at: new Date(),
          user_id: 'current-user',
          artifactCount: event.artifactCount
        });
      }
      this.transformChatSessionsToOptions(this.chatSessions);
      if (isViewing) {
        this.currentSessionId = event.sessionId;
      }
      this.syncSelectedChatOption();
    }

    if (isViewing && event.artifactCount > 0) {
      this._gainsToastService.success('Success', `${event.artifactCount} artifact(s) created`);
    }

    this.shouldAutoScroll = true;
    this._cdr.markForCheck();
  }

  ngAfterViewChecked(): void {
    if (this.shouldAutoScroll) {
      this.scrollToBottom();
    }
  }

  /**
   * Collapses on a press outside the widget — and it must be `mousedown`, not `click`.
   *
   * This host stays mounted whether the widget is open or shut, so the listener is always live. On
   * `click`, anything elsewhere in the app that opens the widget from a click handler was killed by
   * its own gesture: the handler ran, `isOpen` became true, and then the *same* click finished
   * bubbling to `document` and was read as an outside click. That is what stopped the Alerts tray's
   * Ask DEO from ever opening the widget. `mousedown` precedes the click, so the guard below sees
   * `isOpen` still false and does nothing, and the click that follows opens the widget for real.
   *
   * `<gains-tray>` and the Active agents popover both use `mousedown` for the same reason.
   */
  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const isInsideChat = this._elementRef.nativeElement.contains(target);
    const isInsideDropdown = target.closest('.ant-select-dropdown') !== null || target.closest('.cdk-overlay-container') !== null;

    if (this.isOpen && !isInsideChat && !isInsideDropdown) {
      this.collapseChat();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen) {
      this.collapseChat();
    }
  }

  /**
   * Starts the conversation on an anomaly the reviewer asked about.
   *
   * The question is put in the composer rather than sent, so the reviewer can reword it before
   * committing to it.
   */
  /**
   * Opens on the anomaly: the selector goes to its owning agent and the question is put straight
   * away, which is what the prototype's own Ask DEO does —
   * `setState({ deoAgent: a.agent }, () => this.askDeo('Why did ' + a.title + ' get flagged?'))`.
   *
   * It only asks when that agent actually has an answer. Otherwise the question stays in the
   * composer, as it did before the selector existed: sending it would produce the "nothing on that
   * yet" fallback, which is a worse answer than letting the reviewer redirect it to DEO themselves.
   */
  private _applyAlertContext(context: AlertAskDeoContext): void {
    // The tray has already told the service which agent this is; mirroring it here is what puts the
    // selector on that agent before the widget paints.
    this.selectedTargetId = context.agentId;
    this.refreshSessionOptions();
    this.onMessageChange(context.prompt);

    if (this._shouldAnswerLocally()) {
      this.sendMessage();
    }

    this._cdr.markForCheck();
  }

  openChat(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this._dEOAiAssistantService.openChat();
  }

  closeChat(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this._dEOAiAssistantService.closeChat();
    this.currentMessage = '';
    this._baseText = '';
    this.selectedFile = null;
    this.currentSessionId = null;
    // Detach the widget's view without touching the store, so any in-flight stream keeps running
    // and is still there (in the widget or the full screen) when reopened.
    this.currentConversation = null;

    const newChatOption = this.chatOptions.find(s => s.value === 'new-chat');
    if (newChatOption) {
      this.chatSelectControl.setValue(newChatOption, { emitEvent: false });
    }
  }

  collapseChat(): void {
    this._dEOAiAssistantService.collapseChat();
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  beforeUpload = (file: NzUploadFile): boolean => {
    this.selectedFile = this._fileSelectionService.beforeUpload(file, this.MAX_FILE_SIZE);
    this._cdr.markForCheck();
    return false;
  };

  removeFile(): void {
    this.selectedFile = this._fileSelectionService.removeFile();
    this._cdr.markForCheck();
  }

  formatFileSize(bytes: number): string {
    return this._fileSelectionService.formatFileSize(bytes);
  }

  getFileIcon(fileName: string): string {
    return this._fileSelectionService.getFileIcon(fileName);
  }

  onTalkingToSelected(target: TalkingToTarget): void {
    this.selectedTargetId = target.id;
    this._dEOAiAssistantService.setSelectedAgentId(target.id === DEO_TARGET_ID ? null : target.id);
    this.refreshSessionOptions();
    this._cdr.markForCheck();
  }

  /** A follow-up chip asks its question straight away rather than only filling the box. */
  onFollowUpChosen(prompt: string): void {
    if (this.isResponseInProgress) {
      return;
    }

    this.currentMessage = prompt;
    this.sendMessage();
  }

  onMessageChange(text: string): void {
    this.currentMessage = text;
    this._conversationService.setDraft(this.currentConversation?.clientId, text);
  }

  sendMessage(): void {
    const hasValidText = this.currentMessage.trim().length > 0;
    if (!hasValidText || this.isResponseInProgress) {
      return;
    }

    if (!this.currentConversation) {
      this.currentConversation = this._conversationService.startConversation();
    }

    const messageContent = this.currentMessage.trim();
    const attachment = this.selectedFile
      ? {
          name: this.selectedFile.name,
          size: this.selectedFile.size,
          type: this.selectedFile.type
        }
      : undefined;

    this.onMessageChange('');
    this._baseText = '';
    this.selectedFile = null;
    this.shouldAutoScroll = true;

    // A question put to a named agent is answered locally: the backend has no agent routing, so
    // there is nothing to send it to. Everything addressed to DEO goes over the wire as before.
    if (this._shouldAnswerLocally()) {
      this._answerFromAgent(messageContent);
      return;
    }

    this._conversationService.send(this.currentConversation, messageContent, attachment);
  }

  private _shouldAnswerLocally(): boolean {
    const agentId = this.selectedAgent?.id;
    return this.isTalkingToEnabled && !!agentId && this._agentReplyService.canAnswer(agentId);
  }

  /**
   * Hands the turn to DeoConversationService, which answers it from the agent instead of the wire.
   *
   * The conversation's own busy status holds the composer and shows the typing indicator, exactly as
   * a streamed turn does, and its `updated` ticks are what re-render this view.
   */
  private _answerFromAgent(question: string): void {
    const agent = this.selectedAgent;
    if (!this.currentConversation || !agent) {
      return;
    }

    this._conversationService.sendToAgent(this.currentConversation, question, agent);
  }

  /**
   * Cancels the response the user no longer wants to wait for. The service aborts the underlying request
   * and settles the half-finished message; the conversation returns to idle.
   */
  onStopStreaming(): void {
    if (!this.isResponseInProgress || !this.currentConversation) {
      return;
    }
    this._conversationService.stop(this.currentConversation);
    this.currentMessage = this._conversationService.getDraft(this.currentConversation.clientId);
    this._baseText = '';
    this._cdr.markForCheck();
  }

  getDisplayedContent(message: ChatMessage): string {
    return this._conversationService.getDisplayedContent(message);
  }

  /** Says what the backend is doing after the answer text, so a long artifact build never reads as a hang. */
  getActivityLabel(message: ChatMessage): string {
    return formatStreamActivityLabel(message.activity);
  }

  /** Open while the answer is still being worked out, collapsed once it has started, unless the reader chose. */
  isProgressCollapsed(message: ChatMessage): boolean {
    return this._progressCollapsedByUser.get(message) ?? !!message.content;
  }

  toggleProgress(message: ChatMessage): void {
    this._progressCollapsedByUser.set(message, !this.isProgressCollapsed(message));
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  formatTime(date: Date | undefined): string {
    return formatChatMessageTimestamp(date);
  }

  loadChatHistory(sessionId: string): void {
    if (sessionId === 'new-chat') {
      this.currentSessionId = null;
      this.currentConversation = this._conversationService.reuseOrStartConversation(this.currentConversation);
      this._restoreComposer();
      this._cdr.markForCheck();
      return;
    }

    // Binds to the live conversation: an in-flight or already-loaded chat shows as-is; otherwise its
    // history loads into the store (the 'updated' event drives the scroll-to-bottom).
    this.currentSessionId = sessionId;
    this.currentConversation = this._conversationService.openSession(sessionId);
    this._restoreComposer();
    this.shouldAutoScroll = true;
    setTimeout(() => this.scrollToBottom(), 0);
    this._cdr.markForCheck();
  }

  private startNewChatFromClear(): void {
    this.chatSelectControl.setValue(this.NEW_CHAT_OPTION, { emitEvent: false });
    this.loadChatHistory(this.NEW_CHAT_OPTION.value as string);
    this.onMessageChange('');
  }

  /** Each chat keeps its own unsent text; the attachment and dictation never follow the user to another chat. */
  private _restoreComposer(): void {
    this.currentMessage = this._conversationService.getDraft(this.currentConversation?.clientId);
    this._baseText = '';
    this.selectedFile = null;
    if (this.isListening) {
      this._voiceService.stopListening();
    }
  }

  onMessagesScroll(): void {
    this.checkIfAtBottom();
  }

  private checkIfAtBottom(): void {
    if (!this.messagesContainer) {
      return;
    }

    const element = this.messagesContainer.nativeElement;
    const threshold = 50;
    const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - threshold;
    this.shouldAutoScroll = isNearBottom;
  }

  private scrollToBottom(): void {
    if (!this.messagesContainer) {
      return;
    }

    const element = this.messagesContainer.nativeElement;
    element.scrollTop = element.scrollHeight;
    this.shouldAutoScroll = false;
  }

  toggleVoiceInput(): void {
    if (this.isTyping) {
      return;
    }

    if (this.isListening) {
      this._voiceService.stopListening();
      return;
    }

    this.voiceError = null;
    this._baseText = this.currentMessage;

    this._voiceService.startListeningFromConfiguration();
  }

  formatMessageContent(content: string): string {
    return this._messageFormatter.formatContent(content);
  }
}
